/**
 * API Configuration
 *
 * Shared API configuration and utilities
 */

export const API_CONFIG = {
  baseUrl: process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4000',
} as const;

/**
 * Build full API URL with /api/v1 prefix
 */
export function buildApiUrl(path: string): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_CONFIG.baseUrl}/api/v1${cleanPath}`;
}

/**
 * Build full URL for static files (images, documents) stored on the API server
 *
 * @param path - The path returned from the API (e.g., /api/public/avatars/123.webp)
 * @returns Full URL with API base URL prepended for server paths, or the original URL for absolute URLs
 *
 * @example
 * buildStaticUrl('/api/public/avatars/123.webp') // => 'http://localhost:3000/api/public/avatars/123.webp'
 * buildStaticUrl('https://example.com/image.jpg') // => 'https://example.com/image.jpg'
 * buildStaticUrl('/images/logo.svg') // => '/images/logo.svg' (local asset)
 */
export function buildStaticUrl(path: string | undefined | null): string | undefined {
  if (!path) return undefined;

  // Already absolute URL or data URI - return as is
  if (
    path.startsWith('http://') ||
    path.startsWith('https://') ||
    path.startsWith('data:')
  ) {
    return path;
  }

  // Local asset (starts with / but not /api/) - return as is
  if (path.startsWith('/') && !path.startsWith('/api/')) {
    return path;
  }

  // Server-stored file (e.g., /api/public/avatars/...)
  return `${API_CONFIG.baseUrl}${path}`;
}

/**
 * API Error class
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Handle API response
 */
export async function handleApiResponse<T>(response: Response): Promise<T> {
  if (response.ok) {
    if (response.status === 204) {
      // No Content
      return null as unknown as T;
    }

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      return (await response.json()) as T;
    }
    const text = await response.text();
    return text as unknown as T;
  }

  // Error branch
  let payload: Record<string, unknown> | undefined;
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      payload = (await response.json()) as Record<string, unknown>;
    } else {
      const text = await response.text();
      payload = text ? { message: text } : undefined;
    }
  } catch {
    payload = undefined;
  }

  throw new ApiError(
    `API request failed: ${response.status} ${response.statusText} - ${response.url}`,
    response.status,
    payload,
  );
}
